import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Copy, CheckCircle, Link as LinkIcon, CreditCard, Users, MessageSquare } from 'lucide-react';
import { client } from '../api/client';
import { formatDate } from '../lib/localized';

interface DashboardData {
  name: string;
  referralCode: string;
  referralLink: string;
  totalReferrals: number;
  totalLeads: number;
  totalInquiries: number;
  pendingCommission: number;
  approvedCommission: number;
  paidCommission: number;
  recentOrders: Array<{
    bookingId: number;
    bookingToken: string | null;
    orderAmount: number;
    commissionAmount: number;
    status: string;
    createdAt: number;
  }>;
  recentInquiries: Array<{
    type: 'lead' | 'booking' | 'package_booking';
    id: number;
    name: string | null;
    phone: string;
    title: string;
    status: string;
    amount: number | null;
    currency: string | null;
    createdAt: number;
  }>;
}

const statusMap: Record<string, string> = {
  pending: 'referral.status.pending',
  approved: 'referral.status.approved',
  paid: 'referral.status.paid',
  cancelled: 'referral.status.cancelled',
};

const inquiryStatusMap: Record<string, string> = {
  new: 'referral.inquiryStatus.new',
  contacted: 'referral.inquiryStatus.contacted',
  qualified: 'referral.inquiryStatus.qualified',
  converted: 'referral.inquiryStatus.converted',
  archived: 'referral.inquiryStatus.archived',
  pending: 'referral.inquiryStatus.pending',
  confirmed: 'referral.inquiryStatus.confirmed',
  completed: 'referral.inquiryStatus.completed',
  cancelled: 'referral.inquiryStatus.cancelled',
  paid: 'referral.inquiryStatus.paid',
  unpaid: 'referral.inquiryStatus.unpaid',
  partial: 'referral.inquiryStatus.partial',
  refunded: 'referral.inquiryStatus.refunded',
};

const inquiryTypeMap: Record<string, string> = {
  lead: 'referral.inquiryType.lead',
  booking: 'referral.inquiryType.booking',
  package_booking: 'referral.inquiryType.packageBooking',
  experience_inquiry: 'referral.inquiryType.experienceInquiry',
  island_owner_talk: 'referral.inquiryType.islandOwnerTalk',
  inspiration_guide: 'referral.inquiryType.inspirationGuide',
};

export default function ReferralDashboardPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation('referral');
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(t('error.invalidLink'));
      setLoading(false);
      return;
    }
    async function load() {
      try {
        const res = await client.api.fetch(`/api/referral/dashboard/${encodeURIComponent(token)}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || t('error.loadFailed'));
        setData(json.data || null);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('error.loadFailed'));
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [token, t]);

  function copyLink() {
    if (!data?.referralLink) return;
    navigator.clipboard.writeText(data.referralLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  if (loading) {
    return (
      <div className="pt-32 pb-20 text-center text-gray-500">
        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" />
        <p>{t('loading')}</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="pt-32 pb-20 px-4 text-center">
        <div className="max-w-md mx-auto bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
          <p className="text-red-500 font-medium mb-2">{t('error.title')}</p>
          <p className="text-gray-600">{error || t('error.invalidOrExpired')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16 px-4 bg-[#f8fafb] min-h-screen">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-[#0a4c6b] text-white p-6 md:p-8">
            <h1 className="text-2xl md:text-3xl font-bold mb-1">{t('dashboard.title')}</h1>
            <p className="text-white/80">{t('dashboard.welcome', { name: data.name })}</p>
          </div>

          <div className="p-6 md:p-8 border-b border-gray-100">
            <label className="text-sm font-medium text-gray-700 mb-2 block">{t('link.label')}</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-700 truncate flex items-center gap-2">
                <LinkIcon className="w-4 h-4 text-[#2ec4b6] shrink-0" />
                <span className="truncate">{data.referralLink}</span>
              </div>
              <button
                onClick={copyLink}
                className="bg-[#0a4c6b] hover:bg-[#083d56] text-white px-4 py-3 rounded-xl text-sm font-medium transition flex items-center gap-2"
              >
                {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? t('link.copied') : t('link.copy')}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">{t('link.hint')}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-6 md:p-8 border-b border-gray-100">
            <div className="bg-[#f0f9f7] rounded-xl p-4 text-center">
              <Users className="w-5 h-5 text-[#2ec4b6] mx-auto mb-2" />
              <p className="text-xs text-gray-500">{t('stats.referrals')}</p>
              <p className="text-xl font-bold text-[#0a4c6b]">{data.totalReferrals}</p>
            </div>
            <div className="bg-[#f0f9f7] rounded-xl p-4 text-center">
              <MessageSquare className="w-5 h-5 text-[#2ec4b6] mx-auto mb-2" />
              <p className="text-xs text-gray-500">{t('stats.inquiries')}</p>
              <p className="text-xl font-bold text-[#0a4c6b]">{data.totalInquiries}</p>
            </div>
            <div className="bg-[#f0f9f7] rounded-xl p-4 text-center">
              <CreditCard className="w-5 h-5 text-[#2ec4b6] mx-auto mb-2" />
              <p className="text-xs text-gray-500">{t('stats.pending')}</p>
              <p className="text-xl font-bold text-[#0a4c6b]">HK${data.pendingCommission.toLocaleString()}</p>
            </div>
            <div className="bg-[#f0f9f7] rounded-xl p-4 text-center">
              <CreditCard className="w-5 h-5 text-[#2ec4b6] mx-auto mb-2" />
              <p className="text-xs text-gray-500">{t('stats.approved')}</p>
              <p className="text-xl font-bold text-[#0a4c6b]">HK${data.approvedCommission.toLocaleString()}</p>
            </div>
            <div className="bg-[#f0f9f7] rounded-xl p-4 text-center">
              <CreditCard className="w-5 h-5 text-[#2ec4b6] mx-auto mb-2" />
              <p className="text-xs text-gray-500">{t('stats.paid')}</p>
              <p className="text-xl font-bold text-[#0a4c6b]">HK${data.paidCommission.toLocaleString()}</p>
            </div>
          </div>

          <div className="p-6 md:p-8 border-b border-gray-100">
            <h3 className="font-bold text-[#0d1b2a] mb-4">{t('orders.title')}</h3>
            {data.recentOrders.length === 0 ? (
              <p className="text-sm text-gray-400">{t('orders.empty')}</p>
            ) : (
              <div className="space-y-3">
                {data.recentOrders.map((o, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 text-sm">
                    <div>
                      <p className="font-medium text-[#0d1b2a]">{t('orders.orderLabel', { token: o.bookingToken || o.bookingId })}</p>
                      <p className="text-gray-500">{formatDate(o.createdAt)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-[#0a4c6b]">HK${o.commissionAmount.toLocaleString()}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        o.status === 'paid' ? 'bg-green-100 text-green-700' :
                        o.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                        o.status === 'cancelled' ? 'bg-gray-100 text-gray-600' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {t(statusMap[o.status]) || o.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="p-6 md:p-8">
            <h3 className="font-bold text-[#0d1b2a] mb-4">{t('inquiries.title')}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('inquiries.description')}
            </p>
            {data.recentInquiries.length === 0 ? (
              <p className="text-sm text-gray-400">{t('inquiries.empty')}</p>
            ) : (
              <div className="space-y-3">
                {data.recentInquiries.map((item, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 text-sm">
                    <div>
                      <p className="font-medium text-[#0d1b2a]">
                        {item.name || t('inquiries.friend')}
                        <span className="ml-2 text-gray-500">{t('inquiries.phoneSuffix', { phone: item.phone })}</span>
                      </p>
                      <p className="text-gray-500">{formatDate(item.createdAt)}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-[#0a4c6b]">
                        {item.type === 'lead'
                          ? (t(inquiryTypeMap[item.title]) || item.title || t('inquiries.fallbackLead'))
                          : t(inquiryTypeMap[item.type]) || item.type}
                        {item.amount != null && item.currency && (
                          <span className="ml-2 text-xs font-normal text-gray-500">
                            {item.currency}${item.amount.toLocaleString()}
                          </span>
                        )}
                      </p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        item.status === 'paid' || item.status === 'converted' ? 'bg-green-100 text-green-700' :
                        item.status === 'confirmed' || item.status === 'qualified' ? 'bg-blue-100 text-blue-700' :
                        item.status === 'cancelled' || item.status === 'archived' ? 'bg-gray-100 text-gray-600' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {t(inquiryStatusMap[item.status]) || item.status || t('referral.inquiryStatus.new')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <p className="text-center text-gray-400 text-xs mt-8">
          {t('footer')}
        </p>
      </div>
    </div>
  );
}
